/** Compact link into the Help articles: a muted "?" icon that navigates to
 *  /help/<scope>#<story>. Story ids are compile-time checked against the
 *  generated registry, so a renamed story breaks the build, not the link.
 *  Used by PageHeader (`helpStory` prop), dialogs, and FieldHelp. */
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { helpStoryPath, helpStoryTitle } from '@/help/paths';
import type { HelpStoryId } from '@/help/registry.gen';

interface Props {
  story: HelpStoryId;
  size?: 'small' | 'medium';
  /** Open the article in a new tab instead of navigating the app. */
  newTab?: boolean;
}

export function HelpLink({ story, size = 'small', newTab = false }: Props) {
  const title = helpStoryTitle(story);
  return (
    <Tooltip title={`Help: ${title}`}>
      <IconButton
        size={size}
        component={RouterLink}
        to={helpStoryPath(story)}
        {...(newTab && { target: '_blank', rel: 'noopener noreferrer' })}
        aria-label={`Help: ${title}`}
        sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
      >
        <HelpOutlineIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}
